import { createTheme } from '@mui/material/styles';

/**
 * Dira360 design tokens.
 *
 * Keep raw values here and reference them from components so the palette can be
 * changed in one place. The primary colour is taken from the product logo.
 */
export const TOKENS = {
  brand: {
    primary: '#0a56a5',
    primaryLight: '#3f7fc4',
    primaryDark: '#083f7a',
    secondary: '#0f766e'
  },
  status: {
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
    info: '#0288d1'
  },
  radius: {
    sm: 6,
    md: 8,
    lg: 12
  }
};

const lightPalette = {
  mode: 'light',
  primary: {
    main: TOKENS.brand.primary,
    light: TOKENS.brand.primaryLight,
    dark: TOKENS.brand.primaryDark,
    contrastText: '#ffffff'
  },
  secondary: {
    main: TOKENS.brand.secondary,
    contrastText: '#ffffff'
  },
  success: { main: TOKENS.status.success },
  warning: { main: TOKENS.status.warning },
  error: { main: TOKENS.status.error },
  info: { main: TOKENS.status.info },
  background: {
    default: '#f4f6f9',
    paper: '#ffffff'
  },
  text: {
    primary: '#17212b',
    secondary: '#5a6b7b'
  },
  divider: 'rgba(23, 33, 43, 0.12)'
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#4f97e0',
    light: '#82b6ec',
    dark: '#1f6bb5',
    contrastText: '#06121f'
  },
  secondary: {
    main: '#2dd4bf',
    contrastText: '#06121f'
  },
  success: { main: '#4caf50' },
  warning: { main: '#ffa726' },
  error: { main: '#ef5350' },
  info: { main: '#29b6f6' },
  background: {
    default: '#0f141a',
    paper: '#161d26'
  },
  text: {
    primary: '#e7edf4',
    secondary: '#9fb0c0'
  },
  divider: 'rgba(231, 237, 244, 0.14)'
};

const buildComponentOverrides = ({ mode, compact }) => {
  const cellPadding = compact ? '4px 8px' : '12px 16px';
  const buttonPadding = compact ? '6px 12px' : '8px 16px';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Visible keyboard focus everywhere (accessibility baseline).
        '*:focus-visible': {
          outline: `2px solid ${mode === 'dark' ? '#82b6ec' : TOKENS.brand.primary}`,
          outlineOffset: 2
        },
        // Respect users who ask for less motion.
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: TOKENS.radius.md,
          padding: buttonPadding,
          textTransform: 'none',
          fontWeight: 500
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: TOKENS.radius.lg,
          boxShadow: '0 2px 10px rgba(15, 33, 55, 0.08)',
          border: `1px solid ${mode === 'dark' ? 'rgba(231, 237, 244, 0.08)' : 'rgba(23, 33, 43, 0.06)'}`
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: TOKENS.radius.md }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': { borderRadius: TOKENS.radius.md }
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: cellPadding,
          // Digits line up in columns so money/quantity totals stop shifting.
          fontVariantNumeric: 'tabular-nums',
          // Let wrapping/ellipsis work inside flex and table layouts. Without
          // this, long references force columns wider than their container.
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          verticalAlign: 'middle'
        },
        head: {
          fontWeight: 600,
          // Headers used to be `nowrap`, which made every column at least as
          // wide as its longest header word. Wrapping lets fluid tables fit.
          whiteSpace: 'normal',
          lineHeight: 1.3,
          verticalAlign: 'bottom'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        // `height` on a table row behaves as a minimum, so wrapped cells still
        // grow the row instead of being clipped in compact mode.
        root: compact ? { height: 32 } : {}
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          rowGap: 4
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: compact
          ? { minHeight: 52, '@media (min-width:600px)': { minHeight: 52 } }
          : {}
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: compact ? { paddingTop: 2, paddingBottom: 2 } : {}
      }
    },
    MuiTooltip: {
      defaultProps: { arrow: true }
    }
  };
};

/**
 * Build the application theme for a given mode + density.
 *
 * MUI v5 has no `colorSchemes` API, so both palettes are plain objects and the
 * active one is swapped by re-creating the theme (see UiThemeContext).
 */
export const createAppTheme = ({ mode = 'light', density = 'comfortable' } = {}) => {
  const compact = density === 'compact';

  return createTheme({
    palette: mode === 'dark' ? darkPalette : lightPalette,
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2.25rem', fontWeight: 600 },
      h2: { fontSize: '1.85rem', fontWeight: 600 },
      h3: { fontSize: '1.6rem', fontWeight: 600 },
      h4: { fontSize: '1.4rem', fontWeight: 600 },
      h5: { fontSize: '1.2rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 }
    },
    shape: { borderRadius: TOKENS.radius.md },
    spacing: compact ? 6 : 8,
    components: buildComponentOverrides({ mode, compact })
  });
};

// Backwards-compatible default: light + comfortable, as used by the app entry
// point before the theme became configurable.
const theme = createAppTheme();

export default theme;
